import { StatusCodes } from 'http-status-codes';

import ApiService from 'services/ApiService/ApiService';
import ConfigurationService, { ConfigurationKeys } from 'services/ConfigurationService';
import { translate } from 'services/i18nService';
import {
  DELETE_COMMENT_RESPONSE_STATE,
  GET_CHAT_WALL_COMMENTS,
  GET_CHAT_WALL_VISIBILITY,
} from 'services/MessagingService/EventNames';
import createMessagingService from 'services/MessagingService/MessagingService';
import NotificationService from 'services/NotificationService';
import APIStates from 'enum/APIStates';
import parseCatch from 'utils/parseCatch';


const BASE_URL = ConfigurationService.get(ConfigurationKeys.BASE_URL);

const GET_COMMENTS_URL = `${BASE_URL}/rest/v1.0/profile/{username}/comments`;
const ADD_COMMENT_URL = `${BASE_URL}/rest/v1.0/profile/{username}/comments/add`;
const DELETE_COMMENT_URL = `${BASE_URL}/rest/v1.0/profile/{username}/comments/delete`;
const SET_CHAT_WALL_COMMENTS_VISIBILITY_URL = `${BASE_URL}/rest/v1.0/profile/{username}/options`;

class ChatWallService {
  constructor(createMessagingService) {
    this.messagingService = createMessagingService();
  }

  getComments = (username) => {
    const url = ApiService.substitute(GET_COMMENTS_URL, { username });

    ApiService.get(url)
      .then((res) => {
        this.messagingService.emit(GET_CHAT_WALL_COMMENTS, res.data);
        this.messagingService.emit(GET_CHAT_WALL_VISIBILITY, true);
      })
      .catch((error) => {
        if (error.response.status === StatusCodes.FORBIDDEN && error.response.data.code === 'comments_disabled') {
          this.messagingService.emit(GET_CHAT_WALL_VISIBILITY, false);
        }
        parseCatch(error);
      });
  };

  getShowComments = (username) => {
    const URL = ApiService.substitute(
      SET_CHAT_WALL_COMMENTS_VISIBILITY_URL,
      { username: encodeURIComponent(username) }
    );

    ApiService.get(URL)
      .then((res) => {
        this.messagingService.emit(GET_CHAT_WALL_VISIBILITY, res.data.showComments);
      })
      .catch((error) => {
        parseCatch(error);
      });
  };

  toggleVisibilityChatWall = (impersonatedUser, checked) => {
    const URL = ApiService.substitute(
      SET_CHAT_WALL_COMMENTS_VISIBILITY_URL,
      { username: encodeURIComponent(impersonatedUser) }
    );

    const data = { showComments: checked };
    ApiService.put(URL, { data })
      .then(() =>
        NotificationService.create({
          type: NotificationService.type.TOAST,
          text: translate('account.changesSaved'),
          subtype: NotificationService.subtype.SUCCESS,
        }))
      .catch((error) => {
        parseCatch(error);
        NotificationService.create({
          type: NotificationService.type.TOAST,
          text: translate('account.errorGeneric'),
          subtype: NotificationService.subtype.ERROR,
        });
        this.messagingService.emit(GET_CHAT_WALL_VISIBILITY, !data.showComments);
      });
  };

  submitComment = (comment, username) => {
    const url = ApiService.substitute(ADD_COMMENT_URL, { username });
    const commentValue = { comment };

    ApiService.post(url, { data: commentValue })
      .then(() => {
        this.getComments(username, 0);
      })
      .catch((error) => {
        let text = undefined;
        if (error.response.status === StatusCodes.FORBIDDEN) {
          text = translate('usercomments.comments.accessLater');
        } else {
          text = translate('android.toast_server_error');
        }
        NotificationService.create({
          type: NotificationService.type.TOAST,
          text,
          subtype: NotificationService.subtype.ERROR,
        });
      });
  };

  deleteComment = (username, data) => {
    const url = ApiService.substitute(DELETE_COMMENT_URL, { username });
    const { commentId, sender, username: currentUser } = data;
    this.messagingService.emit(DELETE_COMMENT_RESPONSE_STATE, APIStates.PENDING);
    ApiService.put(url, { data: { commentId } })
      .then(() => {
        this.messagingService.emit(DELETE_COMMENT_RESPONSE_STATE, APIStates.LOADED);
        let message = undefined;
        if (sender === currentUser) {
          message = 'v2profile.comments.successfully.deleted';
        } else {
          message = 'v2profile.comments.successfully.deleted.generic';
        }
        NotificationService.create({
          type: NotificationService.type.TOAST,
          text: translate(message),
          subtype: NotificationService.subtype.SUCCESS,
        });
        this.getComments(username, 0);
      })
      .catch((error) => {
        this.messagingService.emit(DELETE_COMMENT_RESPONSE_STATE, APIStates.ERROR);
        NotificationService.create({
          type: NotificationService.type.TOAST,
          text: translate('android.toast_server_error'),
          subtype: NotificationService.subtype.ERROR,
        });
        parseCatch(error);
      });
  };
}

export default new ChatWallService(createMessagingService);
